import type { JSX } from 'react';
import { GraduationCap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DataTableFilters } from '@/shared/components/DataTableFilters';
import { ResultCount } from '@/shared/components/ResultCount';
import { useTableFilters } from '@/shared/hooks/useTableFilters';
import { EducationCards } from './EducationCards';
import { educationTableColumns } from '../config/educationTableColumns';
import type { Education } from '../types/education.types';

interface EducationListProps {
  educations: Education[];
  /** Whether the query ever resolved. Distinguishes a cached empty list from a failed first load. */
  hasData: boolean;
  isLoading: boolean;
  isError?: boolean;
  onEdit: (education: Education) => void;
  onDelete: (educationId: string) => void;
  onAdd: () => void;
}

function EmptyState({ onAdd }: { onAdd: () => void }): JSX.Element {
  return (
    <div className="space-y-4">
      <ResultCount count={0} />
      <div className="text-center py-6">
        <GraduationCap className="mx-auto h-8 w-8 text-muted-foreground mb-2" />
        <p className="text-sm text-muted-foreground mb-3">No education records</p>
        <Button variant="outline" size="sm" onClick={onAdd}>
          Add Education
        </Button>
      </div>
    </div>
  );
}

/**
 * Owns the filter/sort state for educations. This list renders as cards only —
 * there is no table — so sorting is the default (updatedAt desc) rather than
 * header-driven.
 */
export function EducationList({
  educations,
  hasData,
  isLoading,
  isError,
  onEdit,
  onDelete,
  onAdd,
}: EducationListProps): JSX.Element {
  const {
    filteredSorted,
    searchTerm,
    setSearchTerm,
    filters,
    setFilter,
    clearFilters,
    activeFilterCount,
  } = useTableFilters(educations, educationTableColumns);

  if (isLoading) {
    return <div className="text-sm text-muted-foreground">Loading educations...</div>;
  }

  // A failed refetch keeps whatever resolved before, so only report the failure when nothing ever did.
  if (isError && !hasData) {
    return <div className="text-sm text-destructive">Failed to load education records</div>;
  }

  if (educations.length === 0) {
    return <EmptyState onAdd={onAdd} />;
  }

  return (
    <div className="space-y-4">
      <ResultCount count={filteredSorted.length} />
      <DataTableFilters
        columns={educationTableColumns}
        filters={filters}
        activeFilterCount={activeFilterCount}
        onFilterChange={setFilter}
        onClearFilters={clearFilters}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
      />

      <EducationCards educations={filteredSorted} onEdit={onEdit} onDelete={onDelete} />
    </div>
  );
}
